import React, { useEffect, useState } from 'react';
import { inject, observer } from 'mobx-react';
import Link from 'next/link';
import ActiveLink from 'ui/components/ActiveLink';
import { useTranslation } from 'react-i18next';
import { routes } from 'env/routes';
import { useRouter } from 'next/router';
import withCustomComponent from 'ui/components/withCustomComponent';
import { ReferralNotice } from 'ui/_pages/home/ReferralModal/ReferralNotice';
import { CustomerInactiveNotice } from 'ui/components/Header/CustomerInactiveNotice';
import { ReferralModal } from 'ui/_pages/home/ReferralModal/ReferralModal';
import { ImpersonationNotice } from 'ui/components/Header/ImpersonationNotice';
import { AdminNotice } from 'ui/components/AdminNotice';
import { NotificationsMenuItem } from 'ui/components/Header/NotificationsMenuItem';
import _ from 'lodash';
import { UnpaidBookingsNotice } from './UnpaidBookingsNoticeBW';
import GymWaiverExpiringSoonNotice from './GymWaiverExpiringSoonNoticeBW';
import moment from 'moment';
import { clientConfig } from 'env/config';
import { useGymWaiverStatus } from 'ui/hooks/useGymWaiverStatus';
import { usePaymentRoutes } from '@portal/env/hooks/payments/usePaymentRoutes';

const envconfig = clientConfig();

const MainHeader = withCustomComponent('MainHeader')(
  inject(
    'contentStore',
    'authStore',
    'appStore',
    'floorPlanStore',
    'bookingsStore',
    'checkoutStore',
    'coursesStore',
  )(
    observer((props) => {
      const {
        contentStore,
        authStore,
        appStore,
        floorPlanStore,
        bookingsStore,
        checkoutStore,
        coursesStore,
      } = props;

      const { t } = useTranslation();
      const router = useRouter();

      // State for cart animation and basket count
      const [wiggleCart, setWiggleCart] = useState(false);
      const [basketCount, setBasketCount] = useState(
        checkoutStore.basket.length,
      );

      // MobX values
      const {
        publicCustomPages,
        referralDiscounts,
        business,
        businesses,
        configuration,
        showNotices,
        corporateDashboard,
      } = appStore;
      const { isLoggedIn, impersonationToken, customer } = authStore;
      const { resources, resourceGroups } = bookingsStore;

      // Gym waiver status via React Query hook
      const coworkerId = authStore.customer?.Id;

      const { gymWaiverStatus, hideExpiredMessage } = useGymWaiverStatus();

      const hasGymWaiverExpiringSoon = gymWaiverStatus === 'expiring_soon';

      // Cart wiggle effect
      useEffect(() => {
        const currentBasketCount = checkoutStore.basket.length;
        if (basketCount !== currentBasketCount) {
          setWiggleCart(true);
          setBasketCount(currentBasketCount);
          const timer = setTimeout(() => setWiggleCart(false), 3000);
          return () => clearTimeout(timer);
        }
      }, [checkoutStore.basket.length]);
      const paymentRoutes = usePaymentRoutes();
      // Initial data loading
      useEffect(() => {
        if (!authStore.hasLoadedProfiles)
          authStore.loadProfiles().catch(() => null);
        if (!bookingsStore.hasLoadedResources)
          bookingsStore.loadResources().catch(() => null);
        if (!appStore.hasLoadedReferralDiscounts)
          appStore.loadReferralDiscounts().catch(() => null);
        if (!contentStore.hasLoadedFaqArticles)
          contentStore.loadFaqArticles().catch(() => null);
        if (!contentStore.hasLoadedCommunityPerks)
          contentStore.loadCommunityPerks().catch(() => null);
        if (
          checkoutStore.loadFromLocalStorage &&
          typeof checkoutStore.loadFromLocalStorage === 'function'
        ) {
          const result = checkoutStore.loadFromLocalStorage();
          if (result && typeof result.catch === 'function') {
            result.catch(() => null);
          }
        }
      }, [authStore, bookingsStore, appStore, contentStore, checkoutStore]);

      // Move 'All Resources' group to first element
      const resourceGroupsCopy = [...resourceGroups];
      if (
        resourceGroupsCopy.length &&
        resourceGroupsCopy[resourceGroupsCopy.length - 1].groupName === null
      ) {
        const allResourcesGroup = resourceGroupsCopy.pop();
        resourceGroupsCopy.unshift(allResourcesGroup);
      }

      if (!business) return <></>;

      const customBusinessId = envconfig.renoldBusinessId;
      const customBusiness = businesses.find((b) => b.Id == customBusinessId);
      const isCustomBusiness = customer?.HomeSpaceId == customBusinessId;
      const config = appStore.configuration;
      const impersonationTokens = impersonationToken;
      const customerIsInactive =
        customer && customer.Id > 0 && !customer.Active;
      const customerCreatedPlus3Mins = moment
        .utc(customer?.CreatedOnUtc)
        .add(3, 'm')
        .toDate();
      const mo = moment().utc().toDate();
      const showDiscounts =
        referralDiscounts.length > 0 && customer && !customerIsInactive;
      const isAdmin = authStore.me?.CanAccessPlatform;
      const hasUnpaidBookings =
        bookingsStore.unpaidBookings?.BookingsToPay > 0 &&
        bookingsStore.unpaidBookings?.TimeToPay > 0;
      const StripeDirectDebitPaymentsEnabled =
        authStore.customer?.StripeDirectDebitPaymentsEnabled;

      const clearNotices = () => {
        appStore.setShowNotices(false);
      };

      const showUnpaidBookingsNotice =
        hasUnpaidBookings && !StripeDirectDebitPaymentsEnabled;

      const bottomContent =
        showNotices &&
        (impersonationTokens || customerIsInactive || showDiscounts || isAdmin);

      const isHomeDashboard = router.pathname === routes.home;

      return (
        <>
          {appStore.showReferralModal && <ReferralModal />}
          <header
            data-component-name={props.componentName}
            className={`site-header ${
              isLoggedIn ? 'site-header--li' : 'site-header--lo'
            } ${bottomContent ? 'site-header--with-bottom-content' : ''}
        ${isHomeDashboard ? 'isHomeDashboard' : ''}`}
          >
            {isLoggedIn ? (
              <div className={`site-header__content`}>
                {/*  HEADER - Logged In */}
                <div className="site-header__content--left">
                  <button
                    onClick={() => appStore.toggleMainMenuExpanded()}
                    className="toggle-mobile"
                  >
                    <i className="icon-menu"></i>
                  </button>
                  <Link href={routes.home} className="brand">
                    <img
                      src={`${
                        isCustomBusiness
                          ? customBusiness?.NativeHomeUrlWithLanguage
                          : business.NativeHomeUrlWithLanguage
                      }/business/getlogo?h=144&mode=pad`}
                      alt={t(business.Name)}
                      className={isCustomBusiness ? 'custom-header-logo' : ''}
                    />
                  </Link>
                  {/* <LocationsMenu showCurrent={true} /> */}
                </div>
                <div className="site-header__content--right">
                  <ActiveLink href={paymentRoutes.checkout} legacyBehavior>
                    <a
                      className="btn btn-single-icon btn-main-header mr-8 checkout"
                      aria-label={t('Cart')}
                      suppressHydrationWarning
                    >
                      <i className="icon-cart"></i>
                      {checkoutStore.basket.length > 0 && (
                        <span
                          suppressHydrationWarning
                          className={`badge ${wiggleCart ? 'wiggle' : ''}`}
                        >
                          {_.sumBy(checkoutStore.basket, (i) =>
                            parseInt(i.data?.Quantity ?? '1'),
                          )}
                        </span>
                      )}
                    </a>
                  </ActiveLink>

                  {configuration['PublicWebSite.CommunityBoard'] && (
                    <ActiveLink href={routes.private_threads} legacyBehavior>
                      <a className="btn btn-single-icon btn-main-header mr-8 inbox">
                        <i className="icon-inbox"></i>
                      </a>
                    </ActiveLink>
                  )}

                  <NotificationsMenuItem />
                  <div className="customer dropdown">
                    <button
                      className="btn btn-squared btn-black-white dropdown-toggle"
                      data-toggle="dropdown"
                    >
                      <img
                        src={`${business.NativeHomeUrlWithLanguage}/coworker/getavatar/${customer.Id}?cache=${customer.UpdatedOn}&h=32&w=32&noavatar=%2Fcontent%2Fthemes%2Fpublic%2Fdos%2Fimg%2Favatar-default.png`}
                        id="Customer_Avatar_Img"
                        className="avatar"
                        alt={customer.FullName}
                      />
                      <span className="fs-16 pl-12">
                        {customer.CoworkerType == 'Individual'
                          ? customer.FullName
                          : customer.CompanyName}
                      </span>
                      <span className="caret"></span>
                    </button>
                    <div className="dropdown-menu">
                      {resources.length > 0 && (
                        <ActiveLink href={routes.my_bookings} legacyBehavior>
                          <a className="dropdown-item">{t('My bookings')}</a>
                        </ActiveLink>
                      )}
                      {configuration['PublicWebSite.MyAccount'] && (
                        <ActiveLink href={routes.profile} legacyBehavior>
                          <a className="dropdown-item">{t('My account')}</a>
                        </ActiveLink>
                      )}

                      {!corporateDashboard && (
                        <ActiveLink href={routes.plan} legacyBehavior>
                          <a className="dropdown-item">
                            {t('Plans and benefits')}
                          </a>
                        </ActiveLink>
                      )}
                      {!corporateDashboard &&
                        configuration['Members.ViewInvoices'] && (
                          <ActiveLink href={routes.invoices} legacyBehavior>
                            <a className="dropdown-item">{t('Billing')}</a>
                          </ActiveLink>
                        )}
                      {authStore.profiles.length > 1 && (
                        <>
                          <hr className="mt-5 mb-5" />
                          {authStore.profiles.map((p) => (
                            <a
                              key={'profile-' + p.Id}
                              title={t('Change your current profile')}
                              href="#"
                              onClick={() => authStore.selectProfile(p.Id)}
                              className={`${
                                customer.Id == p.Id ? 'active' : ''
                              } mb-5 dropdown-item`}
                            >
                              <img
                                src={`${business.NativeHomeUrlWithLanguage}/coworker/getavatar/${p.Id}?cache=${p.UpdatedOn}&h=32&w=32&noavatar=%2Fcontent%2Fthemes%2Fpublic%2Fdos%2Fimg%2Favatar-default.png`}
                                className="avatar mr-5"
                                alt={p.FullName}
                              />
                              <span>
                                {p.CoworkerType == 1
                                  ? p.FullName
                                  : p.CompanyName}
                              </span>
                            </a>
                          ))}
                          <hr className="mt-5 mb-5" />
                        </>
                      )}
                      <a className="dropdown-item" href={routes.logout}>
                        <i className="icon-log-out"></i>
                        {t('Log out')}
                      </a>
                    </div>
                  </div>
                </div>
                {/*  END HEADER - Logged In */}
              </div>
            ) : (
              <>
                <div
                  className="site-header__shadow navbar-toggler collapse"
                  id="shadow"
                  data-toggle="collapse"
                  data-target="#navbarSupportedContent,#shadow"
                  aria-controls="navbarSupportedContent"
                  aria-expanded="false"
                  aria-label="Toggle navigation"
                ></div>

                <div className="site-header__content">
                  {/*  HEADER - Logged Out */}
                  <nav className="navbar navbar-expand-lg">
                    <Link href={routes.home} className="navbar-brand">
                      <img
                        src={`${business.NativeHomeUrlWithLanguage}/business/getlogo?h=192&mode=pad`}
                        alt={t(business.Name)}
                      />
                    </Link>

                    <div className="d-flex align-items-center">
                      <Link
                        href={paymentRoutes.checkout}
                        className="btn btn-single-icon btn-white d-lg-none d-inline-flex p-8 mr-8"
                        aria-label={t('Cart')}
                      >
                        <i className="icon-cart" />
                        {basketCount > 0 && (
                          <span className="badge">{basketCount}</span>
                        )}
                      </Link>

                      <button
                        className="navbar-toggler"
                        type="button"
                        data-toggle="collapse"
                        data-target="#navbarSupportedContent,#shadow"
                        aria-controls="navbarSupportedContent"
                        aria-expanded="false"
                        aria-label="Toggle navigation"
                      >
                        <i className="icon-menu"></i>
                      </button>
                    </div>

                    <div
                      className="collapse navbar-collapse"
                      id="navbarSupportedContent"
                    >
                      <ul className="navbar-nav mr-auto">
                        {config['PublicWebSite.AboutUs'] && (
                          <li className="nav-item">
                            <Link href={routes.about} className="nav-link">
                              {t('Why {{businessName}}', {
                                businessName: t(business.Name),
                              })}
                            </Link>
                          </li>
                        )}

                        {(config['Blog.Enabled'] ||
                          config['PublicWebSite.Events'] ||
                          config['PublicWebSite.Directory'] ||
                          config['Jitsi.AlwaysOnRooms.Enabled'] ||
                          publicCustomPages.length > 0) && (
                          <li
                            className="nav-item dropdown"
                            id={`nav-community-menu`}
                          >
                            <a
                              href="#"
                              className="nav-link dropdown-toggle"
                              data-toggle="dropdown"
                              role="button"
                              aria-haspopup="true"
                              aria-expanded="false"
                            >
                              {t('Community')} <span className="caret"></span>
                            </a>
                            <ul className="dropdown-menu" role="menu">
                              {config['Blog.Enabled'] && (
                                <li
                                  id={`nav-blog-menu`}
                                  className={
                                    router.pathname.indexOf(routes.posts) > -1
                                      ? 'active'
                                      : ''
                                  }
                                >
                                  <Link
                                    href={routes.posts}
                                    className="dropdown-item"
                                  >
                                    {t('Articles')}
                                  </Link>
                                </li>
                              )}
                              {config['PublicWebSite.Events'] && (
                                <li
                                  id={`nav-events-menu`}
                                  className={
                                    router.pathname.indexOf(routes.events) > -1
                                      ? 'active'
                                      : ''
                                  }
                                >
                                  <Link
                                    href={routes.events}
                                    className="dropdown-item"
                                  >
                                    {t('Events')}
                                  </Link>
                                </li>
                              )}
                              {coursesStore.coursesPage?.Courses?.length >
                                0 && (
                                <li id={`nav-events-courses`}>
                                  <Link
                                    href={routes.courses}
                                    className={`dropdown-item ${
                                      router.pathname.indexOf(routes.courses) >
                                      -1
                                        ? 'active'
                                        : ''
                                    }`}
                                  >
                                    {t('Courses')}
                                  </Link>
                                </li>
                              )}
                              {config['PublicWebSite.Directory'] && (
                                <li
                                  id={`nav-directory-menu`}
                                  className={
                                    router.pathname ===
                                      routes.directory_members ||
                                    router.pathname === routes.directory_teams
                                      ? 'active'
                                      : ''
                                  }
                                >
                                  <Link
                                    href={
                                      config['Directory.DirectoryRecords'] == 2
                                        ? routes.directory_teams
                                        : routes.directory_members
                                    }
                                    className="dropdown-item"
                                  >
                                    {t('Directory')}
                                  </Link>
                                </li>
                              )}
                              {config['Jitsi.AlwaysOnRooms.Enabled'] && (
                                <li
                                  id={`nav-virtual-rooms-menu`}
                                  className={
                                    router.pathname.indexOf(
                                      routes.virtual_rooms,
                                    ) > -1
                                      ? 'active'
                                      : ''
                                  }
                                >
                                  <Link
                                    href={routes.virtual_rooms}
                                    className="dropdown-item"
                                  >
                                    {t('Virtual rooms')}
                                  </Link>
                                </li>
                              )}
                              {publicCustomPages.map((page, i) => (
                                <li
                                  id={`nav-custom-page-${page.Id}`}
                                  key={i}
                                  className={`${
                                    router.pathname === routes.custom_page &&
                                    router.query.page_name == page.Permalink
                                      ? 'active'
                                      : ''
                                  }`}
                                >
                                  <Link
                                    as={routes.custom_page(page.Permalink)}
                                    href={routes.custom_page('[...page_slug]')}
                                    className="dropdown-item"
                                  >
                                    {t(page.Title || page.Name)}
                                  </Link>
                                </li>
                              ))}
                            </ul>
                          </li>
                        )}

                        {config['PublicWebSite.Tour'] &&
                          appStore.coworkingDashboard && (
                            <li className="nav-item">
                              <Link href={routes.tour} className="nav-link">
                                {t('Take a tour')}
                              </Link>
                            </li>
                          )}

                        {resources.length > 0 && (
                          <>
                            {resourceGroupsCopy.length == 1 && (
                              <li className="nav-item">
                                <Link
                                  href={routes.resources}
                                  className="nav-link"
                                >
                                  {t('Bookings')}
                                </Link>
                              </li>
                            )}
                            {resourceGroupsCopy.length > 1 && (
                              <li
                                className="nav-item dropdown"
                                id={`nav-community-menu`}
                              >
                                <a
                                  href="#"
                                  className="nav-link dropdown-toggle"
                                  data-toggle="dropdown"
                                  role="button"
                                  aria-haspopup="true"
                                  aria-expanded="false"
                                >
                                  {t('Bookings')}{' '}
                                  <span className="caret"></span>
                                </a>
                                <ul className="dropdown-menu" role="menu">
                                  {resourceGroupsCopy.map((group) => (
                                    <li
                                      className="nav-item"
                                      key={group.groupName}
                                    >
                                      <Link
                                        href={`${routes.resources}?group=${
                                          group.groupName ?? ''
                                        }`}
                                        className="nav-link"
                                      >
                                        {t(group.groupName) ||
                                          t('All Resources')}
                                      </Link>
                                    </li>
                                  ))}
                                </ul>
                              </li>
                            )}
                          </>
                        )}
                      </ul>

                      <ul className="navbar-nav navbar-action">
                        <li className="nav-item">
                          <Link
                            href={paymentRoutes.checkout}
                            className="btn btn-single-icon btn-white d-none d-lg-inline-flex"
                            aria-label={t('Cart')}
                            suppressHydrationWarning
                          >
                            <i className="icon-cart" />
                            {checkoutStore.basket.length > 0 && (
                              <span className="badge" suppressHydrationWarning>
                                {checkoutStore.basket.length}
                              </span>
                            )}
                          </Link>
                        </li>

                        <li className="spacer"></li>

                        <li className="nav-item mx-4">
                          <Link
                            href={routes.login}
                            className="btn btn-outline-bruntwood-red border-2"
                            id={`nav-sign-in-menu`}
                          >
                            {t('Sign in')}
                          </Link>
                        </li>

                        {config['Members.CanSignup'] && (
                          <Link
                            href={routes.signup}
                            className="btn btn-primary"
                            id={`nav-sign-up-menu`}
                          >
                            <li className="nav-item mx-4">
                              <span className="d-none d-xl-inline">
                                {t('Become a member')}
                              </span>
                              <span className="d-inline d-xl-none">
                                {t('Sign up')}
                              </span>
                            </li>
                          </Link>
                        )}
                      </ul>
                    </div>
                  </nav>
                  {/*  END HEADER - Logged Out */}
                </div>
              </>
            )}

            {bottomContent && (
              <div className={`site-header__bottom-content`}>
                {impersonationTokens && <ImpersonationNotice />}
                {customerIsInactive && <CustomerInactiveNotice />}
                {showDiscounts && <ReferralNotice />}

                {!customerIsInactive &&
                  !impersonationTokens &&
                  !showDiscounts && <>{isAdmin && <AdminNotice />}</>}

                <button className="clear-notices" onClick={clearNotices}>
                  <i className="icon-close"></i>
                </button>
              </div>
            )}
            {showUnpaidBookingsNotice ? (
              <div className="site-notification">
                <UnpaidBookingsNotice />
              </div>
            ) : (
              hasGymWaiverExpiringSoon &&
              !hideExpiredMessage && (
                <div className="site-notification">
                  <GymWaiverExpiringSoonNotice />
                </div>
              )
            )}
          </header>
        </>
      );
    }),
  ),
);

export default MainHeader;
